import { colors } from '../theme/designTokens';

// API 基礎 URL（相對路徑：API 呼叫永遠打到「載入頁面的同一台主機」，支援任何機器瀏覽）
export const API_BASE_URL = process.env.REACT_APP_API_URL || '';
export const WS_BASE_URL = process.env.REACT_APP_WS_URL || `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`;

// 重新整理間隔（毫秒）
export const REFRESH_INTERVALS = {
  DASHBOARD: 10_000,      // Dashboard 10 秒
  DEVICES: 10_000,        // 設備列表 10 秒
  ALARMS: 5_000,          // 警報 5 秒
  VIBRATION: 5_000,       // 振動 5 秒
} as const;

// 狀態色彩
export const STATUS_COLORS = {
  online: colors.statusOnline,
  offline: colors.statusOffline,
  warning: colors.statusWarning,
  normal: colors.statusNormal,
} as const;

// 警報嚴重度色彩（hex：用於自訂樣式）
export const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: colors.severityCritical,
  ALARM: colors.severityAlarm,
  WARNING: colors.severityWarning,
  INFO: colors.severityInfo,
};

// 警報嚴重度 Ant Design Tag color（用於 <Tag color={...}>）
export const SEVERITY_TAG_COLORS: Record<string, string> = {
  CRITICAL: 'red',
  ALARM: 'orange',
  WARNING: 'blue',
  INFO: 'green',
};

// 警報嚴重度標籤
export const SEVERITY_LABELS: Record<string, string> = {
  CRITICAL: '危急',
  ALARM: '警報',
  WARNING: '警告',
  INFO: '資訊',
};

// 設備類型標籤
export const DEVICE_TYPE_LABELS: Record<string, string> = {
  brine: '滷水系統',
  // 馬修滷水系統主 PLC（.110）：滷水主機 BCH 運轉狀態＋滷水泵/區域泵＋共管＋電錶（警報 device_type = dev_key）
  brine_main: '滷水系統',
  cold: '冷藏區',
  // 馬修各冷藏區 PLC（警報 device_type = dev_key）
  cold_1f1: '冷藏區1F-1',
  cold_1f2: '冷藏區1F-2',
  cold_2f: '冷藏區2F',
  ai_box: 'AI 控箱(XR30)',
  vibration: 'AI 振動',
  // 復盛壓縮機（經 RS485 Hub）：設備類型統一用「滷水主機（BCH）」
  compressor: '滷水主機（BCH）',
  system: '系統',
};

// 稽核日誌「目標」類型中文標籤（audit_log target_type，涵蓋全部類型；設備類沿用同一套用詞）
export const TARGET_TYPE_LABELS: Record<string, string> = {
  ai_box: 'AI 控箱(XR30)',
  vibration: 'AI 振動',
  compressor: '滷水主機（BCH）',
  cold_zone: '冷藏區庫溫控制器',
  device: '設備',
  alarm: '警報',
  audit_trail: '稽核軌跡',
  escalation_policy: '警報升級政策',
  export: '匯出',
  report: '報表',
  rule: '警報規則',
  schedule: '排程',
  setting: '系統設定',
  user: '使用者',
};

// 警報類型中文標籤
export const ALARM_TYPE_LABELS: Record<string, string> = {
  // 馬修滷水/冷藏 PLC 警報（Modbus TCP；對照通訊點數表 9 類）。
  // 中文 key = label，與後端 brine_ingest._classify_alarm 寫入的 alarm_type 完全一致，篩選才對得上。
  '跳脫': '跳脫', '總跳脫': '總跳脫', '系統總異常': '系統總異常',
  '通訊異常': '通訊異常', '感測器異常': '感測器異常',
  '高溫警報': '高溫警報', '低溫警報': '低溫警報',
  '消防高溫警報': '消防高溫警報', '振動警報': '振動警報',
  // 復盛壓縮機（BCH，經 RS485 Hub）：保護跳脫歸「跳脫」，其餘運轉異常歸「壓縮機異常」
  '壓縮機異常': '壓縮機異常',
  // AI控箱(XR30)＝NISE110 工業電腦控箱內溫度高溫（system_alarm_service.check_cabinet_temp）
  'NISE110高溫警報': 'NISE110高溫警報',
  // 振動監控（VPA30868 / Prognosis EHI；馬修保留振動分析）
  HIGH_VIBRATION: '高振動', LOW_HEALTH: '健康度低', EHI_SENSOR_OFFLINE: '振動感測器離線',
  SENSOR_TEMP_HIGH: '3軸振動感測器溫度過高',
  // ⚠️ 2026-10-17 補登：2026-10-15 新增此型時只做了 _fire()，漏登本表與 ALARM_TYPE_GROUPS
  //    —— 正是上面那段註解警告的同一個錯誤，隔兩個月又犯一次。
  //    後果已實際發生：警報列表顯示英文原字 "VIBRATION_DATA_STALE"（Frank 截圖指出），
  //    且四張設定卡都看不到它 → 無法關通知，alarm_role_filter 的 default-allow
  //    會把它無條件推播給所有角色。
  VIBRATION_DATA_STALE: '振動資料過期',
  // 2026-10-21：與上者互補 —— 資料有進來，但變頻機轉速讀不到（電錶有負載卻 rpm=0）。
  VIBRATION_RPM_MISMATCH: '振動轉速讀取異常',
  // 平台端判定的溫度警報（非 PLC 警報碼，由監控站 system_alarm_service 自行評估）
  // ⚠️ 2026-08-15 補登：這三型（＋下方 GATEWAY_OFFLINE / MODBUS_TCP_DISCONNECT）先前只做了
  //    _fire() 觸發、沒有登錄到本表與 ALARM_TYPE_GROUPS，導致全站顯示英文原字、
  //    無法篩選、且不會出現在「警報類型標籤 / 嚴重性定義 / 通知過濾 / 通知發布角色」四張設定卡。
  ZONE_AVG_TEMP_HIGH: '冷藏區均溫過高',
  BRINE_TEMP_HIGH: '共管滷水溫度過高', BRINE_TEMP_LOW: '共管滷水溫度過低',
  // 連線監看（system_alarm_service.check_connections；與通訊狀態頁同源）
  PLC_TCP_DISCONNECT: 'PLC 連線中斷', DB_OFFLINE: '資料庫離線', INGEST_STALE: '資料接收逾時',
  MODBUS_TCP_DISCONNECT: 'Modbus TCP 中斷', GATEWAY_OFFLINE: '閘道離線',
  // 系統 / 通知（監控站 system_alarm_service 仍啟用的檢查可能產生）
  // 共管溫差（回水−出水）過小／過大；門檻於系統設定可調
  BRINE_DT_ABNORMAL: '共管溫差異常',
  // AI 每日用電對帳（ai_energy；偏離自動開單＋資料品質通知）
  AI_ENERGY_DEVIATION: 'AI 用電偏離', AI_ENERGY_DATA_GAP: '用電資料品質',
  AI_ENERGY_DEVIATION_SCREENED: 'AI 用電偏離（已篩檢未開單）',
  // 主機儲存空間（system_alarm_service.check_disk_space；門檻於系統設定可調）
  DISK_SPACE_LOW: '儲存空間不足',
  MQTT_BROKER_DISCONNECT: 'MQTT 斷線', LINE_QUOTA_EXCEEDED: 'LINE 配額超額',
  EMAIL_SMTP_FAILURE: 'Email 發送失敗', DISCORD_WEBHOOK_FAIL: 'Discord 發送失敗',
  TELEGRAM_SEND_FAIL: 'Telegram 發送失敗',
  CUSTOM_RULE: '自訂規則警報',
};

/** 合併預設 + 自訂警報類型標籤（自訂覆蓋預設） */
export function mergeAlarmTypeLabels(custom: Record<string, string>): Record<string, string> {
  return { ...ALARM_TYPE_LABELS, ...custom };
}

/** 警報類型分組（UI 顯示用） */
export const ALARM_TYPE_GROUPS: { label: string; types: string[] }[] = [
  // ⚠️ 這份分組是四張設定卡（警報類型標籤／嚴重性定義／通知過濾／通知發布角色）的唯一清單來源。
  //    新增警報類型時若漏登這裡，該類型在四張卡上都不會出現 —— 也就無法改中文名、無法調嚴重度、
  //    無法關通知、無法指定角色（且 alarm_role_filter 為 default-allow，會無條件推播給所有角色）。
  { label: '溫度警報', types: ['高溫警報', '低溫警報', '消防高溫警報', 'NISE110高溫警報',
    'ZONE_AVG_TEMP_HIGH', 'BRINE_TEMP_HIGH', 'BRINE_TEMP_LOW', 'BRINE_DT_ABNORMAL'] },
  { label: '跳脫/異常', types: ['跳脫', '總跳脫', '系統總異常'] },
  { label: '滷水主機（BCH）', types: ['壓縮機異常'] },
  { label: '通訊/感測/振動', types: ['通訊異常', '感測器異常', '振動警報'] },
  { label: '振動監控', types: ['HIGH_VIBRATION', 'LOW_HEALTH', 'EHI_SENSOR_OFFLINE', 'SENSOR_TEMP_HIGH',
    'VIBRATION_DATA_STALE', 'VIBRATION_RPM_MISMATCH'] },
  { label: '連線監看', types: ['PLC_TCP_DISCONNECT', 'DB_OFFLINE', 'INGEST_STALE', 'MODBUS_TCP_DISCONNECT', 'GATEWAY_OFFLINE'] },
  { label: 'AI 用電對帳', types: ['AI_ENERGY_DEVIATION', 'AI_ENERGY_DATA_GAP', 'AI_ENERGY_DEVIATION_SCREENED'] },
  { label: '系統 / 通知', types: ['DISK_SPACE_LOW', 'MQTT_BROKER_DISCONNECT', 'LINE_QUOTA_EXCEEDED', 'EMAIL_SMTP_FAILURE', 'DISCORD_WEBHOOK_FAIL', 'TELEGRAM_SEND_FAIL', 'CUSTOM_RULE'] },
];

// 歷史數據 tag_name 中文標籤（需與監控頁面設備命名一致）
export const TAG_LABELS: Record<string, string> = {
  // 預冷室 (XR60CX)
  'temp_controller_1.p1_temp': '預冷室 #1 庫內溫度',
  'temp_controller_1.p2_temp': '預冷室 #1 盤管溫度',
  'temp_controller_1.set_temp': '預冷室 #1 設定溫度',
  'temp_controller_1.compressor_on': '預冷室 #1 壓縮機啟停',
  'temp_controller_1.defrost_on': '預冷室 #1 除霜啟停',
  'temp_controller_2.p1_temp': '預冷室 #2 庫內溫度',
  'temp_controller_2.p2_temp': '預冷室 #2 盤管溫度',
  'temp_controller_2.set_temp': '預冷室 #2 設定溫度',
  'temp_controller_2.compressor_on': '預冷室 #2 壓縮機啟停',
  'temp_controller_2.defrost_on': '預冷室 #2 除霜啟停',
  // 數據採集紀錄器 (IOT627)
  'iot627_1.ctrl_temp': '數據採集紀錄器 #1 控制溫度',
  'iot627_1.coil_temp': '數據採集紀錄器 #1 盤管溫度',
  'iot627_1.low_pressure': '數據採集紀錄器 #1 低壓壓力',
  'iot627_1.high_pressure': '數據採集紀錄器 #1 高壓壓力',
  'iot627_1.compressor_current': '數據採集紀錄器 #1 壓縮機電流',
  'iot627_2.ctrl_temp': '數據採集紀錄器 #2 控制溫度',
  'iot627_2.coil_temp': '數據採集紀錄器 #2 盤管溫度',
  'iot627_2.low_pressure': '數據採集紀錄器 #2 低壓壓力',
  'iot627_2.high_pressure': '數據採集紀錄器 #2 高壓壓力',
  'iot627_2.compressor_current': '數據採集紀錄器 #2 壓縮機電流',
  // 變頻冷凍主機 (RC-M01XT)
  'hitachi_1.rpm': '變頻冷凍主機 #1 運轉頻率',
  'hitachi_1.high_pressure': '變頻冷凍主機 #1 高壓壓力',
  'hitachi_1.low_pressure': '變頻冷凍主機 #1 低壓壓力',
  'hitachi_1.running_status': '變頻冷凍主機 #1 運轉狀態',
  'hitachi_2.rpm': '變頻冷凍主機 #2 運轉頻率',
  'hitachi_2.high_pressure': '變頻冷凍主機 #2 高壓壓力',
  'hitachi_2.low_pressure': '變頻冷凍主機 #2 低壓壓力',
  'hitachi_2.running_status': '變頻冷凍主機 #2 運轉狀態',
  // AI 控箱溫控器 (XR30)
  'ai_box.current_temp': 'AI 控箱溫控器 目前溫度',
  'ai_box.setpoint_temp': 'AI 控箱溫控器 設定溫度',
  'ai_box.relay_output': 'AI 控箱溫控器 風扇運轉',
  // AI 控箱溫控器 — 舊版 Edge IPC tag 格式（DB 中仍有歷史資料）
  'ai_box_temp_controller.current_temp': 'AI 控箱溫控器 目前溫度',
  'ai_box_temp_controller.setpoint_temp': 'AI 控箱溫控器 設定溫度',
  'ai_box_temp_controller.relay_output': 'AI 控箱溫控器 風扇運轉',
};

/**
 * 取得 tag_name 中文標籤。
 * 優先查詢 deviceCatalogStore（動態，從 devices.yml 生成），
 * 查無時 fallback 到靜態 TAG_LABELS，最後回傳原始名稱。
 */
// 馬修滷水/冷藏設備 key → 中文名（趨勢數據標籤前綴用；metric 本身已是點表中文）
const MASHIU_DEVICE_LABELS: Record<string, string> = {
  brine_main: '滷水系統',
  cold_1f1: '冷藏區1F-1', cold_1f2: '冷藏區1F-2', cold_2f: '冷藏區2F',
  ai_box: 'AI控箱', ai_box_temp_controller: 'AI控箱',
  compressor_id1: 'BCH#1', compressor_id2: 'BCH#2',   // 復盛壓縮機（壓縮機 RS485 Hub）
};

function rawTagLabel(tagName: string): string {
  // 動態 import 避免循環依賴（zustand store 可在 React 外用 getState）
  try {
    const { tagLabels } = require('../stores/deviceCatalogStore').default.getState();
    if (tagLabels[tagName]) return tagLabels[tagName];
  } catch { /* catalog 尚未載入，使用 fallback */ }
  if (TAG_LABELS[tagName]) return TAG_LABELS[tagName];
  // 馬修 tag 格式 {dev_key}.{中文點名}（對齊 Modbus 通訊點數表）→「中文設備 中文點名」
  const dot = tagName.indexOf('.');
  if (dot > 0) {
    const dev = MASHIU_DEVICE_LABELS[tagName.slice(0, dot)];
    if (dev) return `${dev} ${tagName.slice(dot + 1)}`;
  }
  return tagName;
}

/** tag 顯示標籤：統一機台命名「滷水機#N → BCH#N」（與滷水總覽/振動頁一致） */
export function getTagLabel(tagName: string): string {
  return rawTagLabel(tagName)
    .replace(/滷水機#(\d+)/g, 'BCH#$1')
    // 點名顯示別名：BCH 溫控範圍／目標溫度（DB tag 仍為原名，僅顯示改名）
    .replace(/低壓飽和溫度允許偏差/g, '溫控範圍')
    .replace(/低壓飽和溫度目標值/g, '目標溫度');
}

/** 取得所有可用的 tag 選項（合併 catalog + 靜態） */
export function getAllTagLabels(): Record<string, string> {
  let catalogLabels: Record<string, string> = {};
  try {
    catalogLabels = require('../stores/deviceCatalogStore').default.getState().tagLabels || {};
  } catch { /* ignore */ }
  return { ...TAG_LABELS, ...catalogLabels };
}

/**
 * 去重 tag 清單：同一中文標籤只保留第一筆（優先保留新格式）。
 * 避免 DB 同時有 ai_box.current_temp 與 ai_box_temp_controller.current_temp 時
 * 下拉選單出現重複選項。
 */
export function deduplicateTagsByLabel(tags: string[]): string[] {
  const seenLabels = new Set<string>();
  return tags.filter((tag) => {
    const label = getTagLabel(tag);
    if (seenLabels.has(label)) return false;
    seenLabels.add(label);
    return true;
  });
}

// 滷水主機振動機台名稱（machine_id / conn_id → 名稱）。統一命名，避免篩選與圖例對不上。
// BCH#1 = 變頻、BCH#2 = 定頻。
export const BRINE_MACHINE_LABELS: Record<number, string> = {
  1: 'BCH#1 滷水主機·變頻',
  2: 'BCH#2 滷水主機·定頻',
};
export function brineMachineName(id: number): string {
  return BRINE_MACHINE_LABELS[id] ?? `機台 #${id}`;
}

// EHI 健康指數色彩
export const EHI_COLORS = {
  good: colors.ehiGood,
  fair: colors.ehiFair,
  poor: colors.ehiPoor,
  critical: colors.ehiCritical,
} as const;

export function getEhiColor(score: number | null): string {
  if (score === null) return colors.ehiNoData;
  if (score >= 80) return EHI_COLORS.good;
  if (score >= 60) return EHI_COLORS.fair;
  if (score >= 35) return EHI_COLORS.poor;
  return EHI_COLORS.critical;
}

export function getEhiLabel(score: number | null): string {
  if (score === null) return '無資料';
  if (score >= 80) return '正常';
  if (score >= 60) return '提醒';
  if (score >= 35) return '警戒';
  return '危險';
}

// IOT627 故障碼 bitmask 對照表
export const IOT627_FAULT_CODES: { bit: number; code: string; label: string }[] = [
  { bit: 1,     code: 'E1',   label: 'PT1 斷線' },
  { bit: 2,     code: 'E2',   label: 'PT2 斷線' },
  { bit: 4,     code: 'E3',   label: 'PT3 斷線' },
  { bit: 8,     code: 'E4',   label: 'AI1 斷線' },
  { bit: 16,    code: 'E5',   label: 'AI2 斷線' },
  { bit: 32,    code: 'ALT',  label: '低溫警報' },
  { bit: 64,    code: 'AHT',  label: '高溫警報' },
  { bit: 128,   code: 'ALH',  label: '低濕警報' },
  { bit: 256,   code: 'AHH',  label: '高濕警報' },
  { bit: 512,   code: 'ALP',  label: '低壓壓力警報' },
  { bit: 1024,  code: 'AHP',  label: '高壓壓力警報' },
  { bit: 2048,  code: 'AHTC', label: '總電流過高警報' },
  { bit: 4096,  code: 'AHDC', label: '除霜電流過高警報' },
  { bit: 8192,  code: 'AFO',  label: '庫內風機過載' },
  { bit: 16384, code: 'ACO',  label: '壓縮機過載' },
  { bit: 32768, code: 'AAE',  label: '設備異常' },
];

/** 解碼 IOT627 故障碼 bitmask，回傳觸發的故障列表 */
export function decodeIOT627Faults(raw: number | null | undefined): { code: string; label: string }[] {
  if (!raw) return [];
  return IOT627_FAULT_CODES.filter(f => (raw & f.bit) !== 0);
}

/** 解碼為簡短字串，例如 "AHT(高溫警報)" */
export function formatIOT627Faults(raw: number | null | undefined): string {
  const faults = decodeIOT627Faults(raw);
  if (faults.length === 0) return '';
  return faults.map(f => `${f.code}(${f.label})`).join(', ');
}
